import React, { useState, useEffect, createContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, createSession, resetPassword as resetPasswordApi, singnUp } from '../service/api';
import Cookies from 'js-cookie';
import { useModalFeedback } from '../hooks/useModalFeedback';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [loginUser, setLoginUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use um useRef para o interceptor para evitar re-criação
  const interceptorId = React.useRef(null);

  const { showInfo } = useModalFeedback();

  // Função auxiliar para normalizar as roles para um array
  const normalizeRoles = (roles) => {
    if (!roles) return [];
    if (Array.isArray(roles)) return roles;
    if (typeof roles === 'string') {
      return roles.split(',').map(role => role.trim()).filter(Boolean); // .filter(Boolean) remove strings vazias
    }
    return [];
  };

  useEffect(() => {
    const recoveredUser = Cookies.get('user');
    const token = Cookies.get('token');

    if (recoveredUser && token) {
      const parsedUser = JSON.parse(recoveredUser);
      // Normaliza as roles ao recuperar do cookie
      const userWithNormalizedRoles = {
        ...parsedUser,
        roles: normalizeRoles(parsedUser.roles)
      };
      setLoginUser(userWithNormalizedRoles);
      api.defaults.headers.Authorization = `Bearer ${token}`;
      addAuthInterceptor();
    }
    setLoading(false);
  }, []);

  const addAuthInterceptor = () => {
    if (interceptorId.current !== null) return; // Evita múltiplos interceptores

    interceptorId.current = api.interceptors.response.use(
      (response) => response,
      (error) => {
        const originalRequest = error.config;
        const hasAuthHeader = !!originalRequest?.headers?.Authorization;

        if (hasAuthHeader && error.response?.status === 401) {
          showInfo('Sua sessão expirou. Você será redirecionado para a página de login.', 4000);
          logout();
        }

        return Promise.reject(error);
      }
    );
  };

  // Limpar o interceptor quando o componente é desmontado ou quando o usuário faz logout
  useEffect(() => {
    return () => {
      if (interceptorId.current !== null) {
        api.interceptors.response.eject(interceptorId.current);
        interceptorId.current = null;
      }
    };
  }, []); // Roda apenas na montagem/desmontagem inicial

  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await createSession(email, password);
      const { token, user } = response.data;

      // Garante que avatar sempre exista, mesmo que null
      const userWithAvatar = {
        ...user,
        avatar: user.avatar || null
      };

      // Normaliza as roles ANTES de salvar nos cookies e no estado
      const userToStore = {
        ...userWithAvatar,
        roles: normalizeRoles(userWithAvatar.roles)
      };

      Cookies.set('user', JSON.stringify(userToStore));
      Cookies.set('token', token);
      api.defaults.headers.Authorization = `Bearer ${token}`;

      setLoginUser(userToStore);
      addAuthInterceptor();
      setLoading(false);
      navigate("/home/dashboard");
    } catch (error) {
      setLoading(false);
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    Cookies.remove('user');
    Cookies.remove('token');
    api.defaults.headers.Authorization = null;
    setLoginUser(null);
    if (interceptorId.current !== null) { // Ejecta o interceptor no logout
      api.interceptors.response.eject(interceptorId.current);
      interceptorId.current = null;
    }
    navigate("/");
  };

  const resetPassword = async (email, tempPassword, newPassword, confirmNewPassword) => {
    try {
      const response = await resetPasswordApi(email, tempPassword, newPassword, confirmNewPassword);
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  const signUp = async (email, first_name, last_name) => {
    try {
      const response = await singnUp(email, first_name, last_name);
      return response.data;
    } catch (error) {
      console.error('User error:', error);
      throw error;
    }
  };

  const handleOAuthLogin = (user, token) => {
    // Normaliza as roles do usuário OAuth antes de armazenar
    const userToStore = {
      ...user,
      roles: normalizeRoles(user.roles)
    };

    Cookies.set('user', JSON.stringify(userToStore));
    Cookies.set('token', token);
    api.defaults.headers.Authorization = `Bearer ${token}`;

    setLoginUser(userToStore);
    addAuthInterceptor();
    navigate('/home/dashboard');
  };

  // As funções hasRole, hasAnyRole, hasAllRoles agora operarão em um ARRAY de roles
  const hasRole = (role) => {
    return loginUser?.roles?.includes(role);
  };

  const hasAnyRole = (requiredRoles = []) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (!loginUser?.roles) return false;
    return requiredRoles.some(requiredRole => loginUser.roles.includes(requiredRole));
  };

  const hasAllRoles = (requiredRoles = []) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (!loginUser?.roles) return false;
    return requiredRoles.every(requiredRole => loginUser.roles.includes(requiredRole));
  };

  return (
    <AuthContext.Provider value={{ authenticated: !!loginUser, loginUser, loading, login, logout, resetPassword, hasRole, hasAnyRole, hasAllRoles, signUp, api, handleOAuthLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;